import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  TextField,
  Typography,
  MenuItem,
  IconButton,
  InputAdornment,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AddCircleOutline,
  EditOutlined,
  Close,
  Assessment,
  CampaignOutlined,
  AddLinkOutlined,
} from "@mui/icons-material";
import platformServices from "../../../react-query/services/platformServices";
import reportServices from "../../../react-query/services/reportServices";
import paymentportalservice from "../../../services/paymentportalservice";
import CampaignPickerModal from "../CampaignPickerModal";
import AddManualLinksModal from "./AddManualLinksModal";

const INITIAL_PARAMS = { name: "", platform: "", brand: null };

// Self-contained "Create Report" flow — shared by Reports.jsx (the flat list's
// "New Report" button, no groupId) and ReportGroupDashboard.jsx ("+ Add Report"
// inside a parent group, groupId set). Previously this whole flow lived inline
// in Reports.jsx, so adding a report from inside a group meant navigating away
// to /reports and back just to reuse it — extracted here so a group's dashboard
// can open the exact same dialog in place, without leaving the page.
//
// Owns its own platform/brand lookups, campaign picker, and the manual-links
// paste-URLs follow-up modal. The parent only needs to: render this with
// `open`/`onClose`, optionally pass `groupId`/`groupName` to scope creation
// into a group, and handle `onCreated`/`onSuccess` to update its own report
// list/cache and show a success message however it normally does.
const CreateReportDialog = ({ open, onClose, groupId = null, groupName = null, onCreated, onSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [params, setParams] = useState(INITIAL_PARAMS);
  const [platforms, setPlatforms] = useState([]);
  const [brandsList, setBrandsList] = useState([]);
  const [reportMode, setReportMode] = useState("campaign"); // 'campaign' | 'manual'
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [nameAutoFilled, setNameAutoFilled] = useState(false);
  const [campaignPickerOpen, setCampaignPickerOpen] = useState(false);
  const [error, setError] = useState(false);

  // Manual-mode follow-up: paste post URLs right after the report itself is created.
  const [newReportId, setNewReportId] = useState(null);
  const [newReportPlatformName, setNewReportPlatformName] = useState(null);
  const [addPostsAfterCreate, setAddPostsAfterCreate] = useState(false);

  // Platforms/brands are cheap lookups, fetched fresh each time the dialog opens
  // rather than kept alive at the page level for a dialog that's closed most of the time.
  useEffect(() => {
    if (!open) return;
    (async () => {
      const [platformsRes, brandsRaw] = await Promise.all([
        platformServices.FetchPlatforms(),
        paymentportalservice.FetchBrandsData().catch(() => []),
      ]);
      setPlatforms(platformsRes);
      const normalizedBrands = (Array.isArray(brandsRaw) ? brandsRaw : (brandsRaw?.data ?? []))
        .map((b) => ({ _id: String(b._id || b.id || b.brandId || ""), name: b.name || b.brandName || b.title || "" }))
        .filter((b) => b._id && b.name)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
      setBrandsList(normalizedBrands);
    })();
  }, [open]);

  const resetForm = () => {
    setParams(INITIAL_PARAMS);
    setReportMode("campaign");
    setSelectedCampaign(null);
    setNameAutoFilled(false);
    setError(false);
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") setNameAutoFilled(false);
    setParams((prev) => ({ ...prev, [name]: value }));
  };

  const handleCampaignSelect = (campaign) => {
    setSelectedCampaign(campaign);
    setParams((prev) => ({ ...prev, name: campaign.name }));
    setNameAutoFilled(true);
  };

  const handleCampaignClear = () => {
    setSelectedCampaign(null);
    if (nameAutoFilled) {
      setParams((prev) => ({ ...prev, name: "" }));
      setNameAutoFilled(false);
    }
  };

  const handleSubmit = async () => {
    const payload = {
      name: params.name,
      platform: params.platform,
      ...(params.brand ? { brand: { _id: params.brand._id, name: params.brand.name } } : {}),
      ...(reportMode === "campaign" && selectedCampaign ? { campaign: selectedCampaign._id } : {}),
      ...(groupId ? { groupId } : {}),
    };
    const response = await reportServices.AddReport(payload);
    if (response.status === 201) {
      setError(response.data);
      return;
    }

    const newReport = response.data;
    const postsCount = newReport?.posts?.length ?? 0;
    // Capture the platform name for the manual-links modal below BEFORE resetForm()
    // clears `params` — otherwise it'd look up an already-blank platform id.
    const submittedPlatformName = platforms.find((p) => p._id === params.platform)?.name ?? null;

    onCreated?.(newReport);
    resetForm();
    onClose?.();

    if (reportMode === "manual") {
      setNewReportId(newReport._id);
      setNewReportPlatformName(submittedPlatformName);
      setAddPostsAfterCreate(true);
    } else {
      onSuccess?.(
        postsCount > 0
          ? `Report created — ${postsCount} post${postsCount !== 1 ? "s" : ""} imported from campaign. Fetching stats in the background…`
          : "Report created successfully!"
      );
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            border: "1px solid #E5E7EB",
            boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
            width: { xs: "100%", sm: "500px" },
            m: { xs: 1, sm: 2 },
            px: { xs: 2, sm: 3.5 },
            py: { xs: 2, sm: 3 },
          },
        }}
      >
        <Box sx={{ height: 4, background: "linear-gradient(90deg, #FF5200 0%, #FF8C00 100%)", flexShrink: 0 }} />

        <Box
          sx={{
            px: { xs: 2, sm: 3.5 }, pt: 3, pb: 2,
            display: "flex", alignItems: "flex-start", justifyContent: "space-between",
            background: "#fff", borderBottom: "1px solid #F3F4F6",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "11px",
              background: "linear-gradient(135deg, rgba(255,82,0,0.1), rgba(255,140,0,0.05))",
              border: "1px solid rgba(255,82,0,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Assessment sx={{ color: "#FF5200", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#111827", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
                Create Report
              </Typography>
              <Typography sx={{ color: "#9CA3AF", fontSize: 12, mt: 0.3 }}>
                {groupName ? `Adding to “${groupName}”` : "Set up your campaign report"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              mt: -0.5, mr: -0.5, color: "#9CA3AF",
              background: "#F9FAFB", border: "1px solid #E5E7EB",
              "&:hover": { background: "#F3F4F6", color: "#374151" },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3.5 }, py: { xs: 2, sm: 3 }, background: "#fff" }}>
          {/* Mode toggle */}
          <Box sx={{ display: "flex", gap: 1, mb: 3, p: 0.5, bgcolor: "#F3F4F6", borderRadius: "12px" }}>
            {[
              { value: "campaign", label: "Ad Campaign", icon: CampaignOutlined },
              { value: "manual", label: "Manual Links", icon: AddLinkOutlined },
            ].map(({ value, label, icon: Icon }) => (
              <Box
                key={value}
                onClick={() => {
                  setReportMode(value);
                  if (value === "manual") { setSelectedCampaign(null); setNameAutoFilled(false); }
                }}
                sx={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75,
                  py: { xs: 2, sm: 3 }, borderRadius: "10px", cursor: "pointer", transition: "all 0.18s ease",
                  fontWeight: 600, fontSize: 13,
                  ...(reportMode === value
                    ? { bgcolor: "#fff", color: "#FF5200", boxShadow: "0 1px 4px rgba(0,0,0,0.1)", border: "1px solid rgba(255,82,0,0.2)" }
                    : { color: "#6B7280", "&:hover": { color: "#374151" } }),
                }}
              >
                <Icon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "inherit" }}>{label}</Typography>
              </Box>
            ))}
          </Box>

          {/* Campaign — only in campaign mode */}
          <Box sx={{ mb: 2.5, display: reportMode === "campaign" ? "block" : "none" }}>
            <Typography sx={{ color: "#6B7280", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", mb: 1 }}>
              Campaign
            </Typography>

            {selectedCampaign ? (
              <Box sx={{
                background: "rgba(255,82,0,0.05)", border: "1px solid rgba(255,82,0,0.2)", borderRadius: "12px",
                px: { xs: 2, sm: 3.5 }, py: { xs: 2, sm: 3 },
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "linear-gradient(135deg, #FF5200, #FF8C00)", flexShrink: 0 }} />
                  <Typography sx={{ color: "#111827", fontWeight: 500, fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {selectedCampaign.name}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleCampaignClear}
                  sx={{ ml: 1, color: "#9CA3AF", p: "3px", flexShrink: 0, "&:hover": { color: "#EF4444", background: "rgba(239,68,68,0.08)" } }}
                >
                  <Close sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>
            ) : (
              <Box
                onClick={() => setCampaignPickerOpen(true)}
                sx={{
                  border: "1.5px dashed #D1D5DB", borderRadius: "12px",
                  px: { xs: 2, sm: 3.5 }, py: { xs: 2, sm: 3 },
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
                  cursor: "pointer", transition: "all 0.18s ease",
                  "&:hover": { borderColor: "rgba(255,82,0,0.4)", background: "rgba(255,82,0,0.03)" },
                }}
              >
                <AddCircleOutline sx={{ color: "#9CA3AF", fontSize: 18 }} />
                <Typography sx={{ color: "#9CA3AF", fontSize: 13.5 }}>Select a campaign</Typography>
              </Box>
            )}
          </Box>

          {/* Manual mode hint */}
          {reportMode === "manual" && (
            <Box sx={{
              mb: 2.5, px: { xs: 2, sm: 3.5 }, py: { xs: 2, sm: 3 }, borderRadius: "12px",
              bgcolor: "rgba(255,82,0,0.04)", border: "1px dashed rgba(255,82,0,0.25)",
              display: "flex", alignItems: "flex-start", gap: 1,
            }}>
              <AddLinkOutlined sx={{ color: "#FF8C00", fontSize: 17, mt: 0.1, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 12.5, color: "#6B7280", lineHeight: 1.5 }}>
                After creating the report, you'll paste your Instagram or YouTube post URLs.
                Up to <strong>1500 posts</strong> per report.
              </Typography>
            </Box>
          )}

          {/* Report Name */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ color: "#6B7280", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", mb: 1 }}>
              Report Name
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter a name for this report…"
              size={isMobile ? "small" : "medium"}
              name="name"
              value={params.name}
              onChange={handleChange}
              InputProps={{
                endAdornment: nameAutoFilled ? (
                  <InputAdornment position="end">
                    <EditOutlined sx={{ fontSize: 16, color: "#FF8C00" }} />
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: "#F9FAFB", borderRadius: "12px", color: "#111827", fontSize: 14,
                  "& fieldset": { borderColor: "#E5E7EB" },
                  "&:hover fieldset": { borderColor: "#D1D5DB" },
                  "&.Mui-focused fieldset": { borderColor: "#FF5200", borderWidth: "1.5px" },
                },
                "& input::placeholder": { color: "#9CA3AF", opacity: 1 },
              }}
            />
            {nameAutoFilled && (
              <Typography sx={{ fontSize: 11.5, color: "#FF8C00", mt: 0.75, ml: 0.5 }}>
                Auto-filled from campaign · click to edit
              </Typography>
            )}
          </Box>

          {/* Platform */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ color: "#6B7280", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", mb: 1 }}>
              Platform
            </Typography>
            <TextField
              select
              fullWidth
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              name="platform"
              value={params.platform}
              onChange={handleChange}
              SelectProps={{
                displayEmpty: true,
                renderValue: (val) => {
                  if (!val) return <span style={{ color: "#9CA3AF" }}>Choose platform</span>;
                  return platforms.find((p) => p._id === val)?.name ?? val;
                },
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: "12px", border: "1px solid #E5E7EB", boxShadow: "0 8px 24px rgba(0,0,0,0.1)", mt: 0.5,
                      "& .MuiMenuItem-root": {
                        fontSize: 13.5, color: "#374151", py: { xs: 2, sm: 3 },
                        "&:hover": { background: "rgba(255,82,0,0.06)" },
                        "&.Mui-selected": { background: "rgba(255,82,0,0.08)", color: "#FF5200", fontWeight: 600 },
                      },
                    },
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: "#F9FAFB", borderRadius: "12px", color: "#111827", fontSize: 14,
                  "& fieldset": { borderColor: "#E5E7EB" },
                  "&:hover fieldset": { borderColor: "#D1D5DB" },
                  "&.Mui-focused fieldset": { borderColor: "#FF5200", borderWidth: "1.5px" },
                },
                "& .MuiSvgIcon-root": { color: "#9CA3AF" },
              }}
            >
              {platforms.map((option) => (
                <MenuItem key={option._id} value={option._id}>{option.name}</MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Brand */}
          <Box sx={{ mb: 3.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography sx={{ color: "#6B7280", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase" }}>
                Brand
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: "#9CA3AF", fontWeight: 500, bgcolor: "#F3F4F6", px: { xs: 2, sm: 3.5 }, py: 0.25, borderRadius: "6px" }}>
                optional
              </Typography>
            </Box>
            <Autocomplete
              options={brandsList}
              getOptionLabel={(opt) => opt.name}
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
              value={params.brand}
              onChange={(_, val) => setParams((prev) => ({ ...prev, brand: val }))}
              clearOnEscape
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    borderRadius: "12px", border: "1px solid #E5E7EB", boxShadow: "0 8px 24px rgba(0,0,0,0.1)", mt: 0.5,
                    "& .MuiAutocomplete-option": {
                      fontSize: 13.5, color: "#374151", py: "10px !important",
                      "&:hover": { background: "rgba(255,82,0,0.06)" },
                      '&[aria-selected="true"]': { background: "rgba(255,82,0,0.08)", color: "#FF5200", fontWeight: 600 },
                    },
                  },
                },
              }}
              renderInput={(inputParams) => (
                <TextField
                  size={isMobile ? "small" : "medium"}
                  {...inputParams}
                  placeholder={brandsList.length ? "Search brand…" : "No brands available"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      background: "#F9FAFB", borderRadius: "12px", color: "#111827", fontSize: 14,
                      "& fieldset": { borderColor: "#E5E7EB" },
                      "&:hover fieldset": { borderColor: "#D1D5DB" },
                      "&.Mui-focused fieldset": { borderColor: "#FF5200", borderWidth: "1.5px" },
                    },
                    "& input::placeholder": { color: "#9CA3AF", opacity: 1 },
                    "& .MuiSvgIcon-root": { color: "#9CA3AF" },
                  }}
                />
              )}
            />
          </Box>

          {/* Submit */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            sx={{
              background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
              borderRadius: "12px", py: 1.4, fontSize: 14.5, fontWeight: 600, textTransform: "none",
              boxShadow: "0 4px 16px rgba(255,82,0,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)", opacity: 0.9, boxShadow: "0 8px 24px rgba(255,82,0,0.4)" },
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            Create Report
          </Button>

          {error && (
            <Typography sx={{ textAlign: "center", color: "#EF4444", mt: 1.5, fontSize: 12.5 }}>
              {error}
            </Typography>
          )}
        </Box>
      </Dialog>

      <CampaignPickerModal
        open={campaignPickerOpen}
        onClose={() => setCampaignPickerOpen(false)}
        onSelect={handleCampaignSelect}
      />

      <AddManualLinksModal
        open={addPostsAfterCreate}
        onClose={() => { setAddPostsAfterCreate(false); setNewReportId(null); setNewReportPlatformName(null); }}
        reportId={newReportId}
        platform={newReportPlatformName}
        existingPostCount={0}
        onSuccess={() => {
          setAddPostsAfterCreate(false);
          setNewReportId(null);
          setNewReportPlatformName(null);
          onSuccess?.("Report created with posts. Fetching stats in the background…");
        }}
      />
    </>
  );
};

export default CreateReportDialog;
